import React, { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FiChevronDown, FiArrowUp, FiArrowDown } from 'react-icons/fi';
// Imported by path, not through the hooks barrel: that barrel pulls in every hook (and with them
// _constants and _entities), which every <ListTable /> consumer would then have to load.
import useAnchoredPanel from 'hooks/useAnchoredPanel';
import usePopover from 'hooks/usePopover';
import * as Styled from './styles';

const fieldShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  // Optional column name. When present, the global menu groups fields under it so a long
  // all-columns list stays scannable.
  group: PropTypes.string,
  // Optional plain-language hints for what each direction actually does, e.g. asc on
  // `assigned` puts unassigned retailers first. Surfaced as tooltips + in the aria-label.
  ascHint: PropTypes.string,
  descHint: PropTypes.string,
});

/**
 * Sort control with explicit asc/desc per field.
 * `variant="cell"` renders a compact header icon; `variant="global"` renders a labeled
 * "Sort by" button that groups its fields by column and scrolls. Both call onSort(field, order)
 * with order 1 (asc) / -1 (desc).
 *
 * Shared (modules/SortMenu) so list pages get a consistent, accessible sort affordance — notably
 * the only sort control on compact breakpoints, where tables collapse to header-less cards.
 *
 * The menu is portaled to <body> and anchored in viewport coordinates: as an absolutely-positioned
 * child of the <th> it ran off the right edge of the screen on the last columns (Status), and any
 * ancestor with `overflow` could clip it. useAnchoredPanel clamps it into the viewport and flips it
 * above the trigger when there's no room below.
 */
const SortMenu = ({ fields, sortOption, onSort, variant }) => {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  // The menu lives outside the wrapper in the DOM, so the outside-click check must be told that
  // clicks landing in it are still "inside".
  const isExternalInside = useCallback((target) => panelRef.current?.contains(target), []);
  const { open, setOpen, close } = usePopover({
    wrapperRef,
    panelRef,
    isExternalInside,
    triggerSelector: 'button[aria-haspopup="true"]',
  });
  const panelStyle = useAnchoredPanel({
    open,
    triggerRef,
    panelRef,
    align: variant === 'global' ? 'right' : 'left',
  });

  const { sortBy, sortOrder } = sortOption;
  const activeField = fields.find((f) => f.value === sortBy);
  const ActiveArrow = sortOrder === 1 ? FiArrowUp : FiArrowDown;
  const triggerLabel = activeField
    ? `Sort by: ${activeField.label}, ${sortOrder === 1 ? 'ascending' : 'descending'}`
    : 'Sort';

  const pick = (value, order) => {
    onSort(value, order);
    close();
  };

  return (
    <Styled.Root ref={wrapperRef}>
      {variant === 'global' ? (
        <Styled.GlobalTrigger
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          title="Sort"
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={triggerLabel}
        >
          <span className="sort-text">
            <span className="label">Sort:</span> {activeField ? activeField.label : 'None'}
          </span>
          {activeField && <ActiveArrow />}
          <FiChevronDown />
        </Styled.GlobalTrigger>
      ) : (
        <Styled.IconTrigger
          ref={triggerRef}
          type="button"
          active={!!activeField}
          onClick={() => setOpen((o) => !o)}
          title="Sort"
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={triggerLabel}
        >
          {activeField ? <ActiveArrow /> : <FiChevronDown />}
        </Styled.IconTrigger>
      )}

      {open &&
        createPortal(
          <Styled.Menu ref={panelRef} style={panelStyle} role="group" aria-label="Sort options">
            {fields.map((f, index) => (
              <React.Fragment key={f.value}>
                {/* Group heading whenever the column changes — only in the global menu, which lists
                  every sortable value in the table and would otherwise be a flat wall of rows. */}
                {variant === 'global' && f.group && f.group !== fields[index - 1]?.group && (
                  <Styled.MenuGroupLabel>{f.group}</Styled.MenuGroupLabel>
                )}
                <Styled.MenuRow>
                  <Styled.MenuLabel>{f.label}</Styled.MenuLabel>
                  <Styled.DirButton
                    type="button"
                    active={sortBy === f.value && sortOrder === 1}
                    onClick={() => pick(f.value, 1)}
                    title={f.ascHint ? `Ascending — ${f.ascHint}` : 'Ascending'}
                    aria-label={`Sort by ${f.label} ascending${f.ascHint ? ` — ${f.ascHint}` : ''}`}
                    aria-pressed={sortBy === f.value && sortOrder === 1}
                  >
                    <FiArrowUp />
                  </Styled.DirButton>
                  <Styled.DirButton
                    type="button"
                    active={sortBy === f.value && sortOrder === -1}
                    onClick={() => pick(f.value, -1)}
                    title={f.descHint ? `Descending — ${f.descHint}` : 'Descending'}
                    aria-label={`Sort by ${f.label} descending${f.descHint ? ` — ${f.descHint}` : ''}`}
                    aria-pressed={sortBy === f.value && sortOrder === -1}
                  >
                    <FiArrowDown />
                  </Styled.DirButton>
                </Styled.MenuRow>
              </React.Fragment>
            ))}
          </Styled.Menu>,
          document.body,
        )}
    </Styled.Root>
  );
};

SortMenu.propTypes = {
  fields: PropTypes.arrayOf(fieldShape).isRequired,
  sortOption: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.number,
  }).isRequired,
  onSort: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['cell', 'global']),
};

SortMenu.defaultProps = {
  variant: 'cell',
};

export default SortMenu;
